/**
 * EditAddressDialog - Dialog para editar endereço do cliente
 * Inclui busca automática de CEP via ViaCEP
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-10
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCrmCustomers, CrmCustomer } from '@/hooks/useCrmCustomers';
import { Loader2, MapPin, Search } from 'lucide-react';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger para depuração
const logger = createLogger('EditAddressDialog');

// Props do componente
interface EditAddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: CrmCustomer;
  onSuccess?: () => void;
}

// Interface para dados do formulário
interface AddressFormData {
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
}

/**
 * Dialog de edição de endereço do cliente
 */
export function EditAddressDialog({
  open,
  onOpenChange,
  customer,
  onSuccess
}: EditAddressDialogProps) {
  // Estado do formulário
  const [formData, setFormData] = useState<AddressFormData>({
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
  });
  
  // Estado de busca de CEP
  const [isSearchingCep, setIsSearchingCep] = useState(false);
  
  // Estado para rastrear CEP original e evitar toast indevido ao cancelar
  const [originalCep, setOriginalCep] = useState('');
  
  // Hook de atualização do cliente
  const { updateCustomer } = useCrmCustomers();

  // Sincroniza estado quando dialog abre
  useEffect(() => {
    if (open && customer) {
      const customerCep = customer.cep || '';
      setOriginalCep(customerCep); // Guarda CEP original
      setFormData({
        cep: customerCep,
        logradouro: customer.logradouro || '',
        numero: customer.numero || '',
        complemento: customer.complemento || '',
        bairro: customer.bairro || '',
        cidade: customer.cidade || '',
        estado: customer.estado || '',
      });
    }
  }, [open, customer]);

  /**
   * Handler para mudança de campo
   */
  const handleChange = (field: keyof AddressFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Busca endereço pelo CEP usando ViaCEP
   * Só executa se o CEP foi alterado pelo usuário (evita toast ao cancelar)
   */
  const handleCepBlur = async () => {
    const cepLimpo = formData.cep.replace(/\D/g, '');
    const originalCepLimpo = originalCep.replace(/\D/g, '');
    
    // Só busca se o CEP foi alterado E tem 8 dígitos
    if (cepLimpo.length !== 8 || cepLimpo === originalCepLimpo) {
      return; // Não faz nada se CEP não mudou ou é inválido
    }
    
    logger.debug('[EditAddressDialog] Buscando CEP alterado:', cepLimpo);
    setIsSearchingCep(true);
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado');
        logger.warn('[EditAddressDialog] CEP não encontrado:', cepLimpo);
        return;
      }
      
      logger.info('[EditAddressDialog] CEP encontrado:', data);
      
      setFormData(prev => ({
        ...prev,
        logradouro: data.logradouro || prev.logradouro,
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        estado: data.uf || prev.estado,
      }));
      
      // Atualiza CEP original para evitar buscas repetidas
      setOriginalCep(formData.cep);
      
      toast.success('Endereço preenchido automaticamente');
      
      // Foca no campo número após preencher
      const numeroInput = document.querySelector('input[id="numero"]');
      if (numeroInput) {
        (numeroInput as HTMLInputElement).focus();
      }
    } catch (error) {
      logger.error('[EditAddressDialog] Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP');
    } finally {
      setIsSearchingCep(false);
    }
  };

  /**
   * Formata CEP enquanto digita
   */
  const handleCepChange = (value: string) => {
    // Remove não-dígitos
    const cepLimpo = value.replace(/\D/g, '');
    
    // Aplica máscara: 00000-000
    let cepFormatado = cepLimpo;
    if (cepLimpo.length > 5) {
      cepFormatado = `${cepLimpo.slice(0, 5)}-${cepLimpo.slice(5, 8)}`;
    }
    
    handleChange('cep', cepFormatado);
  };

  /**
   * Busca CEP via botão de pesquisa (independente do onBlur)
   */
  const handleCepSearch = async () => {
    const cepLimpo = formData.cep.replace(/\D/g, '');
    
    if (cepLimpo.length !== 8) {
      toast.error('CEP inválido. Digite 8 dígitos.');
      return;
    }
    
    logger.debug('[EditAddressDialog] Buscando CEP via botão:', cepLimpo);
    setIsSearchingCep(true);
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado');
        logger.warn('[EditAddressDialog] CEP não encontrado:', cepLimpo);
        return;
      }
      
      logger.info('[EditAddressDialog] CEP encontrado:', data);
      
      setFormData(prev => ({
        ...prev,
        logradouro: data.logradouro || prev.logradouro,
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        estado: data.uf || prev.estado,
      }));
      
      setOriginalCep(formData.cep);
      toast.success('Endereço preenchido!');
      
      // Foca no campo número
      const numeroInput = document.querySelector('input[name="numero"]');
      if (numeroInput) {
        (numeroInput as HTMLInputElement).focus();
      }
    } catch (error) {
      logger.error('[EditAddressDialog] Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setIsSearchingCep(false);
    }
  };

  /**
   * Handler para salvar alteração
   */
  const handleSave = async () => {
    logger.debug('[EditAddressDialog] Salvando endereço:', { customerId: customer.id, formData });

    try {
      await updateCustomer.mutateAsync({
        id: customer.id,
        cep: formData.cep || null,
        logradouro: formData.logradouro || null,
        numero: formData.numero || null,
        complemento: formData.complemento || null,
        bairro: formData.bairro || null,
        cidade: formData.cidade || null,
        estado: formData.estado || null,
      });
      
      logger.info('[EditAddressDialog] Endereço atualizado com sucesso');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      logger.error('[EditAddressDialog] Erro ao atualizar endereço:', error);
    }
  };

  const isLoading = updateCustomer.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editar Endereço</DialogTitle>
          <DialogDescription>
            Altere os dados de endereço do cliente. O CEP preenche automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Cabeçalho com ícone */}
          <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Endereço
          </h4>
          
          <div className="grid gap-4 sm:grid-cols-6">
            {/* CEP + Botão Pesquisar */}
            <div className="sm:col-span-2">
              <Label htmlFor="cep">CEP</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  id="cep"
                  value={formData.cep}
                  onChange={(e) => handleCepChange(e.target.value)}
                  placeholder="00000-000"
                  maxLength={9}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCepSearch}
                  disabled={isSearchingCep}
                >
                  {isSearchingCep ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            
            {/* Logradouro */}
            <div className="sm:col-span-4">
              <Label htmlFor="logradouro">Logradouro</Label>
              <Input
                id="logradouro"
                className="mt-1.5"
                value={formData.logradouro}
                onChange={(e) => handleChange('logradouro', e.target.value)}
                placeholder="Rua, Avenida..."
              />
            </div>
            
            {/* Número */}
            <div className="sm:col-span-1">
              <Label htmlFor="numero">Número</Label>
              <Input
                id="numero"
                name="numero"
                className="mt-1.5"
                value={formData.numero}
                onChange={(e) => handleChange('numero', e.target.value)}
                placeholder="Nº"
              />
            </div>
            
            {/* Complemento */}
            <div className="sm:col-span-2">
              <Label htmlFor="complemento">Complemento</Label>
              <Input
                id="complemento"
                className="mt-1.5"
                value={formData.complemento}
                onChange={(e) => handleChange('complemento', e.target.value)}
                placeholder="Apto, Sala..."
              />
            </div>
            
            {/* Bairro */}
            <div className="sm:col-span-3">
              <Label htmlFor="bairro">Bairro</Label>
              <Input
                id="bairro"
                className="mt-1.5"
                value={formData.bairro}
                onChange={(e) => handleChange('bairro', e.target.value)}
                placeholder="Bairro"
              />
            </div>
            
            {/* Cidade */}
            <div className="sm:col-span-4">
              <Label htmlFor="cidade">Cidade</Label>
              <Input
                id="cidade"
                className="mt-1.5"
                value={formData.cidade}
                onChange={(e) => handleChange('cidade', e.target.value)}
                placeholder="Cidade"
              />
            </div>
            
            {/* UF */}
            <div className="sm:col-span-2">
              <Label htmlFor="estado">UF</Label>
              <Input
                id="estado"
                className="mt-1.5"
                value={formData.estado}
                onChange={(e) => handleChange('estado', e.target.value.toUpperCase())}
                placeholder="UF"
                maxLength={2}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
